import dayjs from 'dayjs';
import { getDateRangeNum } from 'src/app/shared/utils/date-util';

import { Injectable, inject } from '@angular/core';

import {
  BehaviorSubject,
  type Observable,
  Subject,
  type Subscription,
  combineLatest,
  from,
} from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { FireStoreService } from './fire-store.service';

import type { FsV2DataSpotPart } from '../models/firestore-data.model';
import type {
  FsV1Data,
  FsV2DataCampaign,
  FsV2DataSpot,
} from '../models/firestore-data.model';
import type {
  CampaignDataItem,
  CombinedStreamData,
  DailyAccessDto,
  SpotDataItem,
} from '../repositories/dto/datareport.dto';

// このサービスで扱うデータの型定義
type DataServiceData =
  | DailyAccessDto
  | CampaignDataItem[]
  | SpotDataItem[]
  | FsV1Data
  | FsV2DataCampaign[]
  | FsV2DataSpot[]
  | unknown[]
  | Record<string, unknown>
  | null;

// getCombinedStreamの引数の型定義
export interface DataCollectionRequest {
  name: string;
  isDaily: boolean;
  hasComparison: boolean;
  sheetName?: string;
}

// 日付範囲の型定義
export interface DateRange {
  start: Date | null;
  end: Date | null;
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  // Inject services
  private readonly fsService = inject(FireStoreService);

  private mapID = new BehaviorSubject<string | null>(null); // 現在の mapID
  private fromDate = new BehaviorSubject<string>(
    dayjs().add(-7, 'day').format('YYYY-MM-DD'),
  );
  private toDate = new BehaviorSubject<string>(
    dayjs().add(-1, 'day').format('YYYY-MM-DD'),
  );

  // 日付フィルター同期用のSubject
  private dateRangeSource = new BehaviorSubject<DateRange>({
    start: dayjs().add(-7, 'day').toDate(),
    end: dayjs().add(-1, 'day').toDate(),
  });

  // コンポーネントが購読できる Observable
  dateRange$ = this.dateRangeSource.asObservable();

  // 更新元のコンポーネントを特定するためのID
  private updateSourceId = new Subject<string>();
  updateSourceId$ = this.updateSourceId.asObservable();

  private dataSubjects: { [key: string]: BehaviorSubject<DataServiceData> } =
    {}; // 対象期間のデータ
  private comparisonSubjects: {
    [key: string]: BehaviorSubject<DataServiceData>;
  } = {}; // 過去データ
  private subscriptionCounts: { [key: string]: number } = {};
  private subscriptions: { [key: string]: Subscription } = {};
  private isDailyFlags: { [key: string]: boolean } = {}; // keyごとのisDailyの値を保存
  private hasComparisonFlags: { [key: string]: boolean } = {}; // keyごとのhasComparisonの値を保存

  private generateKey(collection: string, isDaily: boolean): string {
    return `${collection}_${isDaily ? 'daily' : 'total'}`;
  }

  // `mapID` を設定
  setMapID(mapID: string): void {
    if (this.mapID.value !== mapID) {
      this.mapID.next(mapID);
      // 既存の購読中データをすべて再取得
      for (const key of Object.keys(this.subscriptionCounts)) {
        if (this.subscriptionCounts[key] > 0) {
          const [collection, isDaily] = key.split('_');
          const isDailyFlag = isDaily === 'daily';
          if (this.hasComparisonFlags[key]) {
            this.fetchCurrentAndComparisonData(
              collection,
              mapID,
              this.fromDate.value,
              this.toDate.value,
              isDailyFlag,
            );
          } else {
            this.fetchData(
              collection,
              mapID,
              this.fromDate.value,
              this.toDate.value,
              isDailyFlag,
            );
          }
        }
      }
    }
  }

  getMapID(): string {
    return this.mapID.value || '';
  }

  // 日付範囲を更新するメソッド（コンポーネントから呼び出される）
  updateDateRange(dateRange: DateRange, sourceId?: string): void {
    this.dateRangeSource.next(dateRange);

    // sourceIdが提供されている場合は、更新元を通知
    if (sourceId) {
      this.updateSourceId.next(sourceId);
    }

    // 既存のサービスのsetDateRangeも呼び出す
    if (dateRange.start && dateRange.end) {
      const fromStr = dayjs(dateRange.start).format('YYYY-MM-DD');
      const toStr = dayjs(dateRange.end).format('YYYY-MM-DD');
      this.setDateRange(fromStr, toStr);
    }
  }

  // 現在の日付範囲を取得
  getCurrentDateRange(): DateRange {
    return this.dateRangeSource.value;
  }

  // 現在の期間を設定
  setDateRange(from: string, to: string): void {
    this.fromDate.next(from);
    this.toDate.next(to);

    // dateRangeSource も同期して更新（ただし、これが元の更新でなければ）
    const start = dayjs(from).toDate();
    const end = dayjs(to).toDate();

    // 現在の値と異なる場合のみ更新（無限ループ防止）
    const current = this.dateRangeSource.value;
    if (
      !current.start ||
      !current.end ||
      dayjs(current.start).format('YYYY-MM-DD') !== from ||
      dayjs(current.end).format('YYYY-MM-DD') !== to
    ) {
      this.dateRangeSource.next({ start, end });
    }

    // 日付変更に応じて再取得
    for (const key of Object.keys(this.subscriptionCounts)) {
      if (this.subscriptionCounts[key] > 0 && this.mapID.value) {
        const [collection, isDaily] = key.split('_');
        const isDailyFlag = isDaily === 'daily';
        if (this.hasComparisonFlags[key]) {
          this.fetchCurrentAndComparisonData(
            collection,
            this.mapID.value,
            this.fromDate.value,
            this.toDate.value,
            isDailyFlag,
          );
        } else {
          this.fetchData(
            collection,
            this.mapID.value,
            this.fromDate.value,
            this.toDate.value,
            isDailyFlag,
          );
        }
      }
    }
  }

  // 購読開始
  subscribeToData(
    collection: string,
    isDaily = false as boolean,
    hasComparison = true as boolean, // 過去のデータも取得するか
  ): void {
    if (!this.mapID.value) {
      console.error('Error: mapID must be set before subscribing to data.');
      return;
    }

    const key = this.generateKey(collection, isDaily);

    if (!this.dataSubjects[key]) {
      this.dataSubjects[key] = new BehaviorSubject<DataServiceData>(null);
      this.comparisonSubjects[key] = new BehaviorSubject<DataServiceData>(null);
      this.subscriptionCounts[key] = 0;
    }

    this.subscriptionCounts[key]++;
    this.isDailyFlags[key] = isDaily; // isDailyの値を保存
    this.hasComparisonFlags[key] = hasComparison; // hasComparisonの値を保存

    // console.group('subscribeToData');
    // console.log('key:', key);
    // console.log('dataSubjects', this.dataSubjects[key].value);
    // console.log('subscriptionCounts:', this.subscriptionCounts[key]);
    // console.log('mapID:', this.mapID.value);
    // console.log('fromDate:', this.fromDate.value);
    // console.log('toDate:', this.toDate.value);
    // console.log('isDaily:', isDaily);
    // console.groupEnd();

    // FIXME: 以下のケースだと、comparionDataが取得されない
    // リクエストの前提：collection, isDailyの値は同じ
    // リクエスト1. hasComparison=false
    // リクエスト2. hasComparison=true
    // 検証用コード
    // if (collection === 'v1os') {
    //   console.group('subscribeToData');
    //   console.log('hasComparison:', hasComparison);
    //   console.log(this.dataSubjects[key].value);
    //   console.groupEnd();
    // }

    if (
      this.subscriptionCounts[key] === 1 || // 初回購読時にリクエストを実行
      !this.dataSubjects[key].value || // あるいは、値がnullの場合もリクエストを実行
      (hasComparison && !this.comparisonSubjects[key].value)
    ) {
      if (hasComparison) {
        this.fetchCurrentAndComparisonData(
          collection,
          this.mapID.value,
          this.fromDate.value,
          this.toDate.value,
          isDaily,
        );
      } else {
        this.fetchData(
          collection,
          this.mapID.value,
          this.fromDate.value,
          this.toDate.value,
          isDaily,
        );
      }
    }
  }

  // 購読終了
  unsubscribeFromData(collection: string, isDaily: boolean): void {
    const key = this.generateKey(collection, isDaily);
    if (this.subscriptionCounts[key] > 0) {
      this.subscriptionCounts[key]--;
      if (this.subscriptionCounts[key] === 0) {
        this.subscriptions[key]?.unsubscribe();
        delete this.subscriptions[key];
        this.dataSubjects[key].next(null); // 対象期間のデータをクリア
        this.comparisonSubjects[key].next(null); // 過去のデータもクリア
        delete this.isDailyFlags[key]; // isDailyの値を削除
      }
    }
  }

  // 対象期間のデータの Observable を取得
  getData(collection: string, isDaily: boolean): Observable<DataServiceData> {
    const key = this.generateKey(collection, isDaily);

    if (!this.dataSubjects[key]) {
      this.dataSubjects[key] = new BehaviorSubject<DataServiceData>(null);
    }
    return this.dataSubjects[key].asObservable();
  }

  // 過去のデータの Observable を取得
  getComparisonData(
    collection: string,
    isDaily: boolean,
  ): Observable<DataServiceData> {
    const key = this.generateKey(collection, isDaily);
    if (!this.comparisonSubjects[key]) {
      this.comparisonSubjects[key] = new BehaviorSubject<DataServiceData>(null);
    }
    return this.comparisonSubjects[key].asObservable();
  }

  getDateNum(): number {
    return getDateRangeNum(this.fromDate.value, this.toDate.value);
  }

  getFromDate(): string {
    return dayjs(this.fromDate.value).format('YYYY-MM-DD');
  }

  getToDate(): string {
    return dayjs(this.toDate.value).format('YYYY-MM-DD');
  }

  // 指定した期間のデータを取得
  private async fetchData(
    collection: string,
    mapID: string,
    fromDate: string,
    toDate: string,
    isDaily = false as boolean,
  ): Promise<void> {
    const key = this.generateKey(collection, isDaily);
    const promise = this.fsService.isV1Data(collection)
      ? this.fsService.getV1Data(collection, mapID, fromDate, toDate, isDaily)
      : this.fsService.getV2Data(collection, mapID, fromDate, toDate);

    this.subscriptions[key]?.unsubscribe(); // 古いリクエストを停止

    const observable = from(promise).pipe(
      shareReplay(1), // キャッシュを有効化
      catchError((err) => {
        console.error(`Error fetching data for ${key}:`, err);
        return [null];
      }),
    );

    this.subscriptions[key] = observable.subscribe((data) => {
      this.dataSubjects[key].next(data); // 通常データを更新
    });
  }

  // 指定した期間と直前の期間のデータを取得
  private async fetchCurrentAndComparisonData(
    collection: string,
    mapID: string,
    fromDate: string,
    toDate: string,
    isDaily = false as boolean,
  ): Promise<void> {
    const key = this.generateKey(collection, isDaily);

    // 現在の期間を取得
    const promise = this.fsService.isV1Data(collection)
      ? this.fsService.getV1Data(collection, mapID, fromDate, toDate, isDaily)
      : this.fsService.getV2Data(collection, mapID, fromDate, toDate);

    // 直前の期間を取得
    const duration = getDateRangeNum(this.fromDate.value, this.toDate.value);
    const comparisonFromDate = dayjs(this.fromDate.value)
      .subtract(duration, 'day')
      .format('YYYY-MM-DD');
    const comparisonToDate = dayjs(this.toDate.value)
      .subtract(duration, 'day')
      .format('YYYY-MM-DD');

    const promiseComparison = this.fsService.isV1Data(collection)
      ? this.fsService.getV1Data(
          collection,
          mapID,
          comparisonFromDate,
          comparisonToDate,
          isDaily,
        )
      : this.fsService.getV2Data(
          collection,
          mapID,
          comparisonFromDate,
          comparisonToDate,
        );
    this.subscriptions[key]?.unsubscribe(); // 古いリクエストを停止

    const observable = combineLatest([
      from(promise),
      from(promiseComparison),
    ]).pipe(
      shareReplay(1), // 最新1件のキャッシュを有効化
      catchError((err) => {
        console.error(`Error fetching data for ${key}:`, err);
        return [null, null];
      }),
    );

    this.subscriptions[key] = observable.subscribe((data) => {
      this.dataSubjects[key].next(data ? data[0] : {}); // 通常データを更新
      this.comparisonSubjects[key].next(data ? data[1] : {}); // 比較データを更新
    });
  }

  // 複数のデータストリームを結合
  getCombinedStream(
    collections: DataCollectionRequest[],
  ): Observable<CombinedStreamData[]> {
    const currentStreams = collections.map((collection) => {
      this.subscribeToData(
        collection.name,
        collection.isDaily,
        collection.hasComparison,
      );
      return this.getData(collection.name, collection.isDaily);
    });

    const comparisonStreams = collections.map((collection) => {
      return this.getComparisonData(collection.name, collection.isDaily);
    });

    return combineLatest([...currentStreams, ...comparisonStreams]).pipe(
      map((dataList) => {
        const half = dataList.length / 2;
        const currentData = dataList.slice(0, half);
        const comparisonData = dataList.slice(half);

        return currentData.map((current, index) => {
          const comparison = comparisonData[index];
          return {
            collection: collections[index],
            current: current || null,
            comparison: comparison || null,
          };
        });
      }),
    );
  }

  // データをテーブルデータに変換
  convertToTableData(data: DataServiceData, headers: string[]): string[][] {
    const tableData: string[][] = [];
    tableData.push(headers);

    for (const item of data as { [key: string]: unknown }[]) {
      const values: string[] = Object.values(item).map(
        (value: unknown): string => String(value),
      );
      tableData.push(values);
    }

    return tableData;
  }
}
